import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Star, Copy, Trash2, Pencil, ArrowUp, ArrowDown, ChevronUp, ChevronDown } from "lucide-react";
import { adminFetch, fmt } from "@/shop/api";

const TYPE_LABELS = {
  selected_tackle: "Tackle",
  archive: "Archive",
  edition: "Edition",
};

const CATEGORIES = ["Luggage", "Mainline", "Hooklink", "Hooks", "Terminal Tackle", "Rig Tools", "Apparel"];
const ROLES = ["Hero", "Repeat", "Add-on"];

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [typeFilter, setTypeFilter] = useState("selected_tackle");
  const [catFilter, setCatFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [pubFilter, setPubFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkCat, setBulkCat] = useState("Luggage");
  const [bulkStock, setBulkStock] = useState("0");

  const load = () => {
    const q = typeFilter ? `?product_type=${typeFilter}` : "";
    adminFetch(`/api/admin/products${q}`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => toast.error("Caricamento fallito"));
  };

  useEffect(() => {
    load();
  }, [typeFilter]);

  const act = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error("Operazione fallita");
    }
  };

  const bulk = async (action, value) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    await act(async () => {
      const res = await adminFetch("/api/admin/products/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids, action, value }),
      }).then((r) => r.json());
      toast.success(`Aggiornati ${res.modified} prodotti`);
    });
    setSelected(new Set());
  };

  const reorder = (p, dir) => {
    const sorted = [...items].sort((a, b) => a.position - b.position);
    const idx = sorted.findIndex((x) => x.id === p.id);
    const other = sorted[idx + dir];
    if (!other) return;
    act(() => adminFetch("/api/admin/products/reorder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_a: p.id, id_b: other.id }),
    }));
  };

  const filtered = items
    .filter((p) => (catFilter ? p.category === catFilter : true))
    .filter((p) => (roleFilter ? p.role === roleFilter : true))
    .filter((p) => {
      if (stockFilter === "in") return !p.track_inventory || p.inventory_qty > 0;
      if (stockFilter === "zero") return p.track_inventory && p.inventory_qty <= 0;
      if (stockFilter === "low") return p.track_inventory && p.inventory_qty > 0 && p.inventory_qty <= (p.low_stock_threshold ?? 2);
      return true;
    })
    .filter((p) => (pubFilter ? p.status === pubFilter : true))
    .sort((a, b) => a.position - b.position);

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[
          { id: "", label: "Tutti i tipi" },
          { id: "selected_tackle", label: "Tackle" },
          { id: "archive", label: "Archive" },
          { id: "edition", label: "Editions" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`admin-type-${t.id || "all"}`}
            onClick={() => setTypeFilter(t.id)}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-full border ${typeFilter === t.id ? "border-ember text-ember" : "border-ink-border text-mist"}`}
          >
            {t.label}
          </button>
        ))}
        <select data-testid="filter-cat" value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="bg-ink border border-ink-border rounded-full px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mist">
          <option value="">Categoria: tutte</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="filter-role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="bg-ink border border-ink-border rounded-full px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mist">
          <option value="">Ruolo: tutti</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="filter-stock" value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} className="bg-ink border border-ink-border rounded-full px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mist">
          <option value="">Stock: tutti</option>
          <option value="in">Disponibile</option>
          <option value="low">Ultimi pezzi</option>
          <option value="zero">Esaurito</option>
        </select>
        <select data-testid="filter-pub" value={pubFilter} onChange={(e) => setPubFilter(e.target.value)} className="bg-ink border border-ink-border rounded-full px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mist">
          <option value="">Stato: tutti</option>
          <option value="draft">Bozza</option>
          <option value="active">Online</option>
        </select>
        <button
          data-testid="admin-new-product"
          onClick={() => setEditing({ _new: true })}
          className="ml-auto inline-flex items-center gap-2 bg-ember text-ink font-mono text-[10px] uppercase tracking-[0.25em] px-5 py-2.5 rounded-full hover:bg-ember-bright"
        >
          <Plus size={13} strokeWidth={2} /> Nuovo prodotto
        </button>
      </div>

      {selected.size > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2 border border-ember/50 bg-ink-surface rounded-lg px-4 py-3" data-testid="bulk-bar">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ember mr-2">
            {selected.size} selezionati
          </span>
          <button data-testid="bulk-publish" onClick={() => bulk("publish")} className="font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border border-emerald-700 text-emerald-400 hover:bg-emerald-900/30">Pubblica</button>
          <button data-testid="bulk-unpublish" onClick={() => bulk("unpublish")} className="font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border border-ink-border text-mist hover:text-bone">Ritira</button>
          <button data-testid="bulk-feature" onClick={() => bulk("feature", true)} className="font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border border-ember text-ember">Metti in evidenza</button>
          <select
            data-testid="bulk-category"
            value={bulkCat}
            onChange={(e) => setBulkCat(e.target.value)}
            className="bg-ink border border-ink-border rounded-full px-3 py-1.5 font-mono text-[10px] text-mist"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <button data-testid="bulk-category-apply" onClick={() => bulk("category", bulkCat)} className="font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border border-ink-border text-mist hover:text-bone">Cambia categoria</button>
          <input data-testid="bulk-stock" type="number" min="0" value={bulkStock} onChange={(e) => setBulkStock(e.target.value)} className="w-20 bg-ink border border-ink-border rounded-full px-3 py-1.5 font-mono text-[10px]" />
          <button data-testid="bulk-stock-apply" onClick={() => bulk("stock", bulkStock)} className="font-mono text-[10px] uppercase tracking-[0.2em] px-3.5 py-1.5 rounded-full border border-ink-border text-mist hover:text-bone">Aggiorna stock</button>
          <button onClick={() => setSelected(new Set())} className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint hover:text-bone">Deseleziona</button>
        </div>
      )}

      {filtered.length === 0 && (
        <p className="text-sm text-mist py-10">Nessun prodotto con questi filtri.</p>
      )}

      <div className="space-y-2">
        {filtered.map((p) => (
          <div
            key={p.id}
            data-testid={`admin-row-${p.sku}`}
            className="flex flex-wrap items-center gap-3 border border-ink-border bg-ink-surface px-4 py-3"
          >
            <input
              type="checkbox"
              checked={selected.has(p.id)}
              onChange={() => toggleSel(p.id)}
              data-testid={`admin-select-${p.sku}`}
              className="accent-[#E25E2B]"
            />
            <div className="flex flex-col gap-0.5">
              <button data-testid={`admin-up-${p.sku}`} onClick={() => reorder(p, -1)} className="text-faint hover:text-ember" aria-label="Su"><ChevronUp size={13} /></button>
              <button data-testid={`admin-down-${p.sku}`} onClick={() => reorder(p, 1)} className="text-faint hover:text-ember" aria-label="Giù"><ChevronDown size={13} /></button>
            </div>
            <img
              src={p.images?.[0]?.url || "/photos/tre-canne.jpg"}
              alt=""
              className="w-12 h-12 object-cover border border-ink-border"
            />
            <div className="flex-1 min-w-[170px]">
              <p className="font-serif text-sm">{p.name}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-faint mt-0.5">
                {p.sku} · {TYPE_LABELS[p.product_type]} · {p.brand || p.manufacturer || "—"} · {p.category || "—"} · {p.role || "—"}
              </p>
            </div>
            <span className="font-mono text-[10px] text-faint w-20 text-right" title="Costo (interno)">
              {fmt(p.cost_price || 0)}
            </span>
            <span className="font-mono text-xs w-20 text-right">{fmt(p.price)}</span>
            <span className={`font-mono text-[10px] w-14 text-right ${p.track_inventory && p.inventory_qty <= 0 ? "text-ember" : "text-mist"}`}>
              {p.track_inventory ? `${p.inventory_qty} pz` : "∞"}
            </span>
            <span
              className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${
                p.status === "active" ? "border-emerald-700 text-emerald-400" : "border-faint text-faint"
              }`}
            >
              {p.status === "active" ? "Online" : "Bozza"}
            </span>
            <div className="flex gap-1.5">
              <button
                data-testid={`admin-feature-${p.sku}`}
                title="In evidenza"
                onClick={() => act(() => adminFetch(`/api/admin/products/${p.id}`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ featured: !p.featured }),
                }).then((r) => r.json()))}
                className={`p-2 border rounded-full transition-colors ${p.featured ? "border-ember text-ember" : "border-ink-border text-faint hover:text-mist"}`}
              >
                <Star size={13} strokeWidth={1.5} fill={p.featured ? "currentColor" : "none"} />
              </button>
              <button
                data-testid={`admin-edit-${p.sku}`}
                title="Modifica"
                onClick={() => setEditing(p)}
                className="p-2 border border-ink-border rounded-full text-mist hover:text-bone hover:border-faint"
              >
                <Pencil size={13} strokeWidth={1.5} />
              </button>
              <button
                data-testid={`admin-duplicate-${p.sku}`}
                title="Duplica"
                onClick={() => act(() => adminFetch(`/api/admin/products/${p.id}/duplicate`, { method: "POST" }).then((r) => r.json()).then(() => toast.success("Duplicato")))}
                className="p-2 border border-ink-border rounded-full text-mist hover:text-bone hover:border-faint"
              >
                <Copy size={13} strokeWidth={1.5} />
              </button>
              <button
                data-testid={`admin-delete-${p.sku}`}
                title="Elimina"
                onClick={() => {
                  if (window.confirm(`Eliminare ${p.name}?`))
                    act(() => adminFetch(`/api/admin/products/${p.id}`, { method: "DELETE" }).then(() => toast.success("Eliminato")));
                }}
                className="p-2 border border-ink-border rounded-full text-faint hover:text-ember hover:border-ember"
              >
                <Trash2 size={13} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ProductEditor
          product={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

const inp = "w-full bg-ink border border-ink-border rounded-lg px-3.5 py-2.5 text-sm text-bone focus:outline-none focus:border-ember";

function ProductEditor({ product, onClose, onSaved }) {
  const isNew = !!product._new;
  const [f, setF] = useState({
    name: product.name || "",
    sku: product.sku || "",
    brand: product.brand || "",
    brand_slug: product.brand_slug || "",
    product_type: product.product_type || "selected_tackle",
    collection: product.collection || "Selected Tackle",
    category: product.category || "",
    role: product.role || "",
    position: product.position ?? 1000,
    image_status: product.image_status || "",
    price: product.price ?? 0,
    cost_price: product.cost_price ?? 0,
    inventory_qty: product.inventory_qty ?? 0,
    track_inventory: product.track_inventory ?? true,
    low_stock_threshold: product.low_stock_threshold ?? 2,
    status: product.status || "draft",
    featured: product.featured || false,
    featured_home: product.featured_home || false,
    short_description: product.short_description || "",
    description: product.description || "",
    why_we_stock_it: product.why_we_stock_it || "",
    why_we_stock_it_it: product.why_we_stock_it_it || "",
    specifications: product.specifications || [],
    images: product.images || [],
    seo_title: product.seo_title || "",
    seo_description: product.seo_description || "",
    related_episode: product.related_episode || "",
    manufacturer: product.manufacturer || "",
    model: product.model || "",
    era: product.era || "",
    condition: product.condition || "",
    condition_note: product.condition_note || "",
    components: product.components || "",
    modifications: product.modifications || "",
    defects: product.defects || "",
    service_history: product.service_history || "",
    provenance: product.provenance || "",
    keep_visible_after_sale: product.keep_visible_after_sale || false,
    edition_of: product.edition_of || "",
    edition_size: product.edition_size || 0,
    release_label: product.release_label || "",
    release_date: product.release_date || "",
    preorder: product.preorder || false,
    sold_out: product.sold_out || false,
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [saving, setSaving] = useState(false);
  const isArchive = f.product_type === "archive";
  const isEdition = f.product_type === "edition";

  const upload = async (files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminFetch("/api/admin/upload", { method: "POST", body: fd });
      if (!res.ok) {
        toast.error(`Upload fallito: ${file.name}`);
        continue;
      }
      const d = await res.json();
      setF((s) => ({ ...s, images: [...s.images, { url: d.url, alt: "" }], image_status: "ready" }));
    }
  };

  const moveImage = (i, dir) => {
    const next = [...f.images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set("images", next);
  };

  const save = async () => {
    if (!f.name.trim()) {
      toast.error("Il nome è obbligatorio");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await adminFetch("/api/admin/products", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(f),
        }).then((r) => r.json());
        toast.success("Prodotto creato");
      } else {
        await adminFetch(`/api/admin/products/${product.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(f),
        }).then((r) => r.json());
        toast.success("Salvato");
      }
      onSaved();
    } catch (e) {
      toast.error("Salvataggio fallito");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/95 overflow-y-auto" data-testid="admin-editor">
      <div className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-serif font-light text-3xl">
            {isNew ? "Nuovo prodotto" : `Modifica — ${product.name}`}
          </h2>
          <button data-testid="editor-close" onClick={onClose} className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint hover:text-ember">
            Chiudi ✕
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Nome *</label>
            <input data-testid="editor-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">SKU</label>
            <input data-testid="editor-sku" className={inp} value={f.sku} onChange={(e) => set("sku", e.target.value)} placeholder="generato se vuoto" />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Tipologia</label>
            <select data-testid="editor-type" className={inp} value={f.product_type} onChange={(e) => set("product_type", e.target.value)}>
              <option value="selected_tackle">Selected Tackle</option>
              <option value="archive">Archive</option>
              <option value="edition">Edition</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Marca</label>
            <input data-testid="editor-brand" className={inp} value={f.brand} onChange={(e) => { set("brand", e.target.value); set("brand_slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")); }} />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Categoria</label>
            <select data-testid="editor-category" className={inp} value={f.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">—</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Ruolo Stillwater (interno)</label>
            <select data-testid="editor-role" className={inp} value={f.role} onChange={(e) => set("role", e.target.value)}>
              <option value="">—</option>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Posizione (ordinamento manuale)</label>
            <input data-testid="editor-position" type="number" className={inp} value={f.position} onChange={(e) => set("position", parseInt(e.target.value) || 0)} />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Prezzo € (IVA inclusa)</label>
            <input data-testid="editor-price" type="number" step="0.01" min="0" className={inp} value={f.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Costo (trade, solo interno)</label>
            <input data-testid="editor-cost" type="number" step="0.01" min="0" className={inp} value={f.cost_price} onChange={(e) => set("cost_price", parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Giacenza</label>
            <input data-testid="editor-qty" type="number" min="0" className={inp} value={f.inventory_qty} onChange={(e) => set("inventory_qty", parseInt(e.target.value) || 0)} />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-6 pt-2">
            {[
              ["track_inventory", "Gestisci inventario"],
              ["featured", "In evidenza (rubrica)"],
              ["featured_home", "In homepage (Selected Objects)"],
              ...(isArchive ? [["keep_visible_after_sale", "Resta visibile dopo la vendita (SOLD)"]] : []),
              ...(isEdition ? [["preorder", "Preordine"], ["sold_out", "Sold out"]] : []),
            ].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2.5 cursor-pointer">
                <input
                  data-testid={`editor-${k}`}
                  type="checkbox"
                  checked={f[k]}
                  onChange={(e) => set(k, e.target.checked)}
                  className="accent-[#E25E2B]"
                />
                <span className="text-xs text-mist">{label}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Stato pubblicazione</label>
            <select data-testid="editor-status" className={inp} value={f.status} onChange={(e) => set("status", e.target.value)}>
              <option value="draft">Bozza (non visibile)</option>
              <option value="active">Online</option>
            </select>
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Stato immagini</label>
            <select data-testid="editor-image-status" className={inp} value={f.image_status} onChange={(e) => set("image_status", e.target.value)}>
              <option value="">—</option>
              <option value="to_upload">Da caricare</option>
              <option value="ready">Pronte</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Collega a episodio (opz.)</label>
            <select data-testid="editor-episode" className={inp} value={f.related_episode} onChange={(e) => set("related_episode", e.target.value)}>
              <option value="">— Nessuno —</option>
              {["EP.01","EP.02","EP.03","EP.04","EP.05","EP.06","EP.07","EP.08"].map((e) => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Descrizione breve</label>
            <input data-testid="editor-short" className={inp} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">
              {isArchive ? "L'OGGETTO — descrizione editoriale" : "Descrizione"}
            </label>
            <textarea data-testid="editor-description" rows={5} className={inp} value={f.description} onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">
              WHY WE STOCK IT — nota editoriale (italiano)
            </label>
            <textarea data-testid="editor-why-it" rows={4} className={inp} value={f.why_we_stock_it_it} onChange={(e) => set("why_we_stock_it_it", e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">
              Why we stock it (en, opzionale)
            </label>
            <textarea data-testid="editor-why" rows={3} className={inp} value={f.why_we_stock_it} onChange={(e) => set("why_we_stock_it", e.target.value)} />
          </div>

          {isArchive && (
            <div className="md:col-span-2 border border-ink-border rounded-lg p-5 space-y-4 bg-ink-surface/50">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">Campi Archive</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input data-testid="editor-manufacturer" placeholder="Produttore" className={inp} value={f.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} />
                <input data-testid="editor-model" placeholder="Modello" className={inp} value={f.model} onChange={(e) => set("model", e.target.value)} />
                <input data-testid="editor-era" placeholder="Epoca (es. anni '90)" className={inp} value={f.era} onChange={(e) => set("era", e.target.value)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <select data-testid="editor-condition" className={inp} value={f.condition} onChange={(e) => set("condition", e.target.value)}>
                  <option value="">Condizione…</option>
                  <option value="collector">Collector</option>
                  <option value="excellent">Eccellente</option>
                  <option value="used">Usato</option>
                  <option value="workhorse">Workhorse</option>
                </select>
                <input data-testid="editor-components" placeholder="Componenti originali inclusi" className={inp} value={f.components} onChange={(e) => set("components", e.target.value)} />
              </div>
              <textarea data-testid="editor-condition-note" rows={3} placeholder="Descrizione onesta della condizione" className={inp} value={f.condition_note} onChange={(e) => set("condition_note", e.target.value)} />
              <textarea data-testid="editor-modifications" rows={2} placeholder="Modifiche" className={inp} value={f.modifications} onChange={(e) => set("modifications", e.target.value)} />
              <textarea data-testid="editor-defects" rows={2} placeholder="Difetti noti" className={inp} value={f.defects} onChange={(e) => set("defects", e.target.value)} />
              <textarea data-testid="editor-service" rows={2} placeholder="Storico manutenzioni" className={inp} value={f.service_history} onChange={(e) => set("service_history", e.target.value)} />
              <textarea data-testid="editor-provenance" rows={2} placeholder="Provenienza / note storiche" className={inp} value={f.provenance} onChange={(e) => set("provenance", e.target.value)} />
            </div>
          )}

          {isEdition && (
            <div className="md:col-span-2 border border-ink-border rounded-lg p-5 space-y-4 bg-ink-surface/50">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember">Campi Edition</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input data-testid="editor-edition-of" placeholder="Edition of 50" className={inp} value={f.edition_of} onChange={(e) => set("edition_of", e.target.value)} />
                <input data-testid="editor-edition-size" type="number" min="0" placeholder="Dimensione edizione" className={inp} value={f.edition_size} onChange={(e) => set("edition_size", parseInt(e.target.value) || 0)} />
                <input data-testid="editor-release-label" placeholder="Stillwater Edition 001" className={inp} value={f.release_label} onChange={(e) => set("release_label", e.target.value)} />
                <input data-testid="editor-release-date" placeholder="Data di uscita (es. Novembre 2025)" className={inp} value={f.release_date} onChange={(e) => set("release_date", e.target.value)} />
              </div>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-3">
              Fotografie (carica, riordina, alt text — la prima è la copertina)
            </label>
            <input
              data-testid="editor-upload"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => upload(Array.from(e.target.files || []))}
              className="block w-full text-xs text-mist file:mr-4 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-ember file:text-ink file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em]"
            />
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="editor-images">
              {f.images.map((im, i) => (
                <div key={im.url + i} className="border border-ink-border">
                  <div className="relative">
                    <img src={im.url} alt="" className="aspect-square w-full object-cover" />
                    <div className="absolute inset-x-0 top-0 flex justify-center gap-1 py-1 bg-ink/70">
                      <button data-testid={`img-up-${i}`} onClick={() => moveImage(i, -1)} className="text-bone/70 hover:text-ember text-xs px-1">▲</button>
                      <button data-testid={`img-down-${i}`} onClick={() => moveImage(i, 1)} className="text-bone/70 hover:text-ember text-xs px-1">▼</button>
                      <button
                        data-testid={`img-del-${i}`}
                        onClick={() => set("images", f.images.filter((_, j) => j !== i))}
                        className="text-bone/70 hover:text-ember text-xs px-1"
                      >
                        ✕
                      </button>
                    </div>
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 font-mono text-[8px] uppercase tracking-[0.2em] bg-ember text-ink px-1.5 py-0.5">
                        Copertina
                      </span>
                    )}
                  </div>
                  <input
                    data-testid={`img-alt-${i}`}
                    placeholder="Alt text (descrizione foto)"
                    value={im.alt || ""}
                    onChange={(e) => {
                      const next = [...f.images];
                      next[i] = { ...next[i], alt: e.target.value };
                      set("images", next);
                    }}
                    className="w-full bg-ink border-t border-ink-border px-2.5 py-2 text-[11px] text-bone focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-2 border-t border-ink-border pt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">SEO title</label>
              <input data-testid="editor-seo-title" className={inp} value={f.seo_title} onChange={(e) => set("seo_title", e.target.value)} />
            </div>
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">SEO description</label>
              <input data-testid="editor-seo-desc" className={inp} value={f.seo_description} onChange={(e) => set("seo_description", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="mt-10 flex gap-3 pb-16">
          <button
            data-testid="editor-save"
            onClick={save}
            disabled={saving}
            className="bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-8 py-3.5 rounded-full hover:bg-ember-bright disabled:opacity-50"
          >
            {saving ? "Salvo…" : "Salva prodotto"}
          </button>
          <button onClick={onClose} className="font-mono text-[11px] uppercase tracking-[0.25em] border border-ink-border px-8 py-3.5 rounded-full text-mist hover:text-bone">
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}
